import React, {
  useContext,
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector, batch } from 'react-redux';
import moment from 'moment';
import CustomHelmet from 'components/CustomHelmet';
import { ExportToCsv } from 'export-to-csv';
import { selectLocale } from 'models/localization';
import { selectLocalizedEventName } from 'models/virtual_event';
import {
  fetchContactList,
  selectAllContacts,
  ContactsSlice,
} from 'models/contacts';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import FooterV2 from 'components/footer_v2';
import { UserSessionContext } from 'components/UserSession';
import ContactListContainer from './ContactListContainer';
import ContactsHeader from './ContactsHeader';

const RootContainer = styled.div`
  height: 100%;
  overflow: auto;
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 960px) {
    background-color: #F0F2F5;
  }

  @media (max-width: 960px) {
    background-color: #F0F1F2;
  }
`;

const PageContentContainer = styled.div`
  flex: 1 0 auto;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ContactsPageContainer = () => {
  const { t } = useTranslation();
  const [scrollableTarget, setScrollableTarget] = useState();
  const pageWrapperRef = useRef();
  const dispatch = useDispatch();
  const { userSession } = useContext(UserSessionContext);
  const contacts = useSelector(selectAllContacts);
  const eventName = useSelector(selectLocalizedEventName);
  const locale = useSelector(selectLocale);
  const { pageNames } = useSelector((state) => state.eventConfigs);
  const getContactRole = (customAttendeeFields) => {
    /* eslint-disable camelcase */
    const vexpoOrganizer = customAttendeeFields?.vexpo_organizer;
    const vexpoRole = customAttendeeFields?.vexpo_role;
    if (vexpoOrganizer) return 'organizer';
    switch (vexpoRole?.toLowerCase()) {
      case 'organizer':
      case 'exhibitor':
        return vexpoRole?.toLowerCase();
      default:
        return 'attendee';
    }
  };

  const formattedContacts = () => contacts.map((row) => {
    const customAttendeeFields = row?.contact?.customAttendeeFields;
    const { vexpo_chat_user_description: fallbackDescription } = customAttendeeFields;
    return ({
      Name: row.contact.name || 'Empty',
      Email: row.contact.email || 'Empty',
      Role: getContactRole(row?.contact?.customAttendeeFields) || 'Attendee',
      'User Description': row?.contact?.vexpoChatUserDescription || fallbackDescription || 'Empty',
      'Created At': moment.utc(row.createdAt, 'YYYY-MM-DD').format('DD MMM YYYY UTC'),
    });
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pageTitle = useMemo(() => pageNames.contact?.[locale] || t('page.contacts', 'My Contacts'), [t, locale]);

  const exportContactData = () => {
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      title: 'test csv',
      useTextFile: false,
      useBom: true,
      filename: 'Business Contacts',
      useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(formattedContacts());
  };

  const fetchMore = useCallback(() => {
    dispatch(fetchContactList(userSession?.rongcloudUserId));
  }, [dispatch]);

  useEffect(() => {
    setScrollableTarget(pageWrapperRef.current);
  }, []);

  useEffect(() => {
    batch(() => {
      dispatch(ContactsSlice.actions.resetContacts());
      fetchMore();
    });
  }, [dispatch, fetchMore]);

  return (
    <ScrollableContainerWrapper WrapperContainer={RootContainer}>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`${pageTitle} | ${eventName}`}</title>
      </CustomHelmet>
      <PageContentContainer>
        <ContactsHeader
          contactCount={contacts?.length ?? 0}
          exportContactData={exportContactData}
        />
        <ContactListContainer
          scrollableTarget={scrollableTarget}
        />
      </PageContentContainer>
      <FooterContainer>
        <FooterV2 hashState="/contacts" />
      </FooterContainer>
    </ScrollableContainerWrapper>
  );
};

export default ContactsPageContainer;
